import { filterClasses } from "@swivl-mono/helpers";
import { TaskState, TicketTask } from "@swivl-mono/types";
import React, { useState } from "react";
import { IoIosMore, IoMdInformationCircleOutline } from "react-icons/io";
import { RiAddLargeFill } from "react-icons/ri";

import NotificationsModel from "../../../Models/Notifications/Notifications.Model";
import { useTicketingStore } from "../../../Models/Ticketing/Ticketing.Store";
import { TaskItem } from "./TaskItem";
import { TicketPanelLayout } from "./TicketPanelLayout";
import { lockableClick } from "./fields/TicketPropertyRow";
import { enforceTaskCharLimit } from "./taskTextUtils";

interface TaskListProps {
  /** Render as an inline section (no panel wrapper / own scroll) for the flyout. */
  embedded?: boolean;
}

export function TaskList({ embedded }: TaskListProps = {}) {
  const tasks = useTicketingStore((s) => s.tasks);
  const ticket = useTicketingStore((s) => s.ticket);
  const restrictedMode = useTicketingStore((s) => s.restrictedMode);
  const addTask = useTicketingStore((s) => s.addTask);
  const setTaskState = useTicketingStore((s) => s.setTaskState);
  const deleteTask = useTicketingStore((s) => s.deleteTask);
  const updateTaskDescription = useTicketingStore((s) => s.updateTaskDescription);

  const [editTaskId, setEditTaskId] = useState<number | null>(null);
  const [showAddInput, setShowAddInput] = useState(false);
  const [taskText, setTaskText] = useState("");

  // Anonymous actors can't accept a ticket, so skip that gate for them.
  const needsAcceptance = !restrictedMode && ticket?.escalationState === "open-to-group";

  const handleTaskTextChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setTaskText(enforceTaskCharLimit(e.target.value));
  };

  const handleAddTask = () => {
    if (taskText.trim()) {
      void addTask(taskText.trim());
      setTaskText("");
      setShowAddInput(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleAddTask();
    } else if (e.key === "Escape") {
      setShowAddInput(false);
      setTaskText("");
    }
  };

  const handleToggleState = (task: TicketTask, completed: boolean) => {
    if (needsAcceptance) return; // panel disabled until the ticket is accepted
    void setTaskState(task.id, completed ? TaskState.COMPLETED : TaskState.UNCOMPLETED);
  };

  const handleSaveInlineEdit = (taskId: number, description: string) => {
    void updateTaskDescription(taskId, description);
    setEditTaskId(null);
  };

  const handleDeleteTask = async (taskId: number): Promise<void> => {
    await deleteTask(taskId);
    void NotificationsModel.createToastNotification({ message: "", title: "You just deleted a task.", autoDismissMs: 5000 });
  };

  const addButton = (
    <button
      onClick={lockableClick(restrictedMode, "Creating tasks", () => setShowAddInput(true))}
      disabled={needsAcceptance}
      className={filterClasses(
        "flex h-8 w-8 items-center justify-center rounded transition",
        needsAcceptance || restrictedMode
          ? "cursor-not-allowed text-grey-400"
          : "cursor-pointer text-grey-900 hover:text-orange-500 active:text-orange-500"
      )}
      type="button"
      title={needsAcceptance ? "Accept the ticket to add tasks" : "Add task"}
    >
      <RiAddLargeFill />
    </button>
  );

  // Accept-first gate — the panel is disabled until the ticket is accepted.
  const gateBanner = needsAcceptance && (
    <div className="mx-4 mt-3 flex items-center gap-2 rounded border border-yellow-700 bg-yellow-100 p-3">
      <IoMdInformationCircleOutline className="h-4 w-4 shrink-0 text-yellow-900" />
      <span className="body-sm text-yellow-900">Accept this ticket to work on tasks</span>
    </div>
  );

  const counter = tasks.length > 0 && (
    <div className="body-sm px-4 pt-3 text-grey-600">
      {tasks.filter((t) => t.state === TaskState.COMPLETED).length} of {tasks.length} complete
    </div>
  );

  const addInputRow = showAddInput && (
    <div className="px-4 pt-3">
      <div className="flex min-h-[42px] items-center gap-3 rounded border border-orange-500 bg-white px-3 py-2 ring-2 ring-orange-50">
        <div className="h-4 w-4 shrink-0 rounded-sm border border-grey-500 bg-white" />

        <input
          type="text"
          value={taskText}
          onChange={handleTaskTextChange}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            if (taskText.trim()) {
              handleAddTask();
            } else {
              setShowAddInput(false);
              setTaskText("");
            }
          }}
          placeholder="New Item"
          autoFocus
          className="body-sm min-w-0 flex-1 bg-transparent p-0 text-black outline-none placeholder:text-grey-500"
        />

        <div className="flex h-6 w-6 shrink-0 items-center justify-center text-grey-900">
          <IoIosMore className="h-4 w-4 -rotate-90" />
        </div>
      </div>
    </div>
  );

  const taskItems =
    tasks.length === 0 ? (
      <div className="body-sm flex h-14 items-center justify-center rounded bg-grey-50 text-grey-600">There are currently no tasks.</div>
    ) : (
      tasks.map((task) => (
        <TaskItem
          key={task.id}
          task={task}
          isEditing={editTaskId === task.id}
          onToggleState={(completed) => handleToggleState(task, completed)}
          onStartAction={() => {}}
          onEdit={() => setEditTaskId(task.id)}
          onSaveEdit={(desc) => handleSaveInlineEdit(task.id, desc)}
          onCancelEdit={() => setEditTaskId(null)}
          onDelete={() => void handleDeleteTask(task.id)}
          locked={restrictedMode}
        />
      ))
    );

  // Embedded: a plain section (used inside the ticket flyout) — a bordered
  // "Tasks" header + the list flowing in the parent's scroll (no own scroll).
  if (embedded) {
    return (
      <div className="w-full">
        <div className="flex h-12 items-center justify-between border-t border-grey-200 px-4">
          <span className="heading-base-alt text-black">Tasks</span>
          {addButton}
        </div>
        {gateBanner}
        {counter}
        {addInputRow}
        <div className={filterClasses("space-y-2 px-4 py-3", needsAcceptance && "pointer-events-none cursor-not-allowed")}>{taskItems}</div>
      </div>
    );
  }

  return (
    <TicketPanelLayout headerLeft={<span className="heading-base-alt text-black">Tasks</span>} headerRight={addButton}>
      {gateBanner}
      {counter}
      {addInputRow}

      {/* Task list — non-interactive until the ticket is accepted (outer shows
          the not-allowed cursor, inner blocks all clicks/drag). */}
      <div className={filterClasses("flex min-h-0 flex-1 flex-col", needsAcceptance && "cursor-not-allowed")}>
        <div className={filterClasses("min-h-0 flex-1 space-y-2 overflow-auto px-4 py-3", needsAcceptance && "pointer-events-none")}>{taskItems}</div>
      </div>
    </TicketPanelLayout>
  );
}
